import React, { useEffect } from 'react';
import { Box, Typography, Chip, IconButton, Button, capitalize } from '@mui/material';
import { Close } from '@mui/icons-material';
import styles from './ViewPassRequest.module.css';
import { Check, X } from 'lucide-react';
import { convertToAmPmRange, safeToAmPm } from 'views/StudentGatePass/components/GatePassRequestTable';

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return { backgroundColor: '#FEF9C3', color: '#CA8A04' };
    case 'rejected':
      return { backgroundColor: '#FEE2E2', color: '#DC2626' };
    case 'resolved':
    case 'approved':
      return { backgroundColor: '#DCFCE7', color: '#16A34A' };
    default:
      return { backgroundColor: '#F3E8FF', color: '#9333EA' };
  }
};

export const formatDateToDashStyle = (inputDate) => {
  if (!inputDate) return 'Not Available (N/A)';
  try {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      .replace(/\s/g, '-');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};
// function formatDateTime24(dateString) {
//   const date = new Date(dateString);

//   const options = {
//     timeZone: "Asia/Kolkata",
//     day: "2-digit",
//     month: "2-digit",
//     year: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//     second: "2-digit",
//     hour12: false
//   };

//   return new Intl.DateTimeFormat("en-IN", options).format(date);
// }
const formatDateTime = (dateInput) => {
  if (!dateInput) return "Not Available (N/A)";

  const date = new Date(dateInput);
  if (isNaN(date)) return "Not Available (N/A)";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");

  // Convert to 12-hour format and add AM/PM
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? String(hours).padStart(2, "0") : "12"; // Handle 12 AM/PM case

  return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
};





const ViewPassRequest = ({ open, onClose, request, onReject, onApprove }) => {
  useEffect(() => {
    console.log('view pass request: ', request);
  }, [request]);

  if (!open || !request) return null;

  const student = request.student || {};
  const approver =
    request.approvedByObject ||
    (typeof request.approvedBy === 'object' && request.approvedBy !== null ? request.approvedBy : null) ||
    request.rejectedByObject ||
    (typeof request.rejectedBy === 'object' && request.rejectedBy !== null ? request.rejectedBy : null) ||
    {};
  const approvalLogs = Array.isArray(request.approvalLogs) ? request.approvalLogs : [];

  const getLatestDepartmentApproval = (departmentKey) => {
    const departmentLogs = approvalLogs.filter(
      (log) => String(log?.department || '').trim().toLowerCase() === String(departmentKey || '').toLowerCase()
    );

    if (departmentLogs.length === 0) return null;

    return departmentLogs.reduce((latest, current) => {
      const latestTime = new Date(latest?.approvedAt || 0).getTime();
      const currentTime = new Date(current?.approvedAt || 0).getTime();
      return currentTime > latestTime ? current : latest;
    });
  };

  const academicApproval = getLatestDepartmentApproval('academic');
  const hostelApproval = getLatestDepartmentApproval('hostel');

  const getApproverName = (log) => {
    if (!log?.approvedBy) return 'Not Available (N/A)';
    if (typeof log.approvedBy?.name === 'string' && log.approvedBy.name.trim()) return log.approvedBy.name;
    const fullName = `${log.approvedBy?.firstName || ''} ${log.approvedBy?.lastName || ''}`.trim();
    return fullName || 'Not Available (N/A)';
  };

  const getApproverRole = (log) => {
    const role = log?.approvedBy?.role;
    return role ? capitalize(role) : 'Not Available (N/A)';
  };

  return (
    <Box className={styles.modalOverlay} onClick={onClose}>
      <Box className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Box className={styles.modalHeader}>
          <Typography className={styles.modalTitle}>Request Details</Typography>
          <Box className={styles.statusSection}>
            <Chip label={request.status} style={getStatusColor(request.status)} className={styles.statusChip} />
            <IconButton onClick={onClose} className={styles.closeButton} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Overview */}
        <Box className={styles.studentOverview}>
          <Box className={styles.avatarSection}>
            <Box className={styles.studentInfo}>
              <Typography className={styles.studentEmail}>Request ID</Typography>
              <Typography className={styles.studentName}>{request.requestId || 'Not Available (N/A)'}</Typography>
            </Box>
            <Box className={styles.studentInfo}>
              <Typography className={styles.studentEmail}>Request Date</Typography>
              <Typography className={styles.studentName}>
                {request.submittedAt ? formatDateTime(request.submittedAt) : 'Not Available (N/A)'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Student Details */}
        <Box className={styles.detailsSection}>
          <Typography className={styles.sectionTitle}>Student Details</Typography>
          <Box className={styles.detailsGrid}>
            <div className={`${styles.detailsNameGrid} ${styles.detailsGrid2}`}>
              <Box className={styles.detailItem}>
                <Typography className={styles.detailLabel}>Student Name</Typography>
                <Typography className={styles.detailValue}>
                  {student?.name || `${student?.firstName || ''} ${student?.lastName || ''}`.trim() || 'Not Available (N/A)'}
                </Typography>
              </Box>
              <Box className={styles.detailItem}>
                <Typography className={styles.detailLabel}>UID</Typography>
                <Typography className={styles.detailValue}>{student?.UID || 'Not Available (N/A)'}</Typography>
              </Box>
              <Box className={styles.detailItem}>
                <Typography className={styles.detailLabel}>Category</Typography>
                <Typography className={styles.detailValue}>{capitalize(student?.category || 'Not Available (N/A)')}</Typography>
              </Box>
              <Box className={styles.detailItem}>
                <Typography className={styles.detailLabel}>Mobile number</Typography>
                <Typography className={styles.detailValue}>{request.student?.phoneNumber || 'Not Available (N/A)'}</Typography>
              </Box>
              <Box className={styles.detailItem}>
                <Typography className={styles.detailLabel}>Building No.</Typography>
                <Typography className={styles.detailValue}>{student?.building_no || 'Not Available (N/A)'}</Typography>
              </Box>
            </div>
          </Box>
        </Box>

        {/* Request Details */}
        <Box className={styles.detailsSection}>
          <Typography className={styles.sectionTitle}>Request Details</Typography>
          <Box className={styles.detailsGrid}>
            <div className={styles.detailsNameGrid}>
              <Box className={styles.detailItem2}>
                <Typography className={styles.detailLabel}>Type</Typography>
                <Typography className={styles.detailValue}>{request.requestType || 'Not Available (N/A)'}</Typography>
              </Box>
            </div>

            {(request.requestType === 'Late Entry' || request.requestType === 'Early Departure') && (
              <>
                {/* DATE */}
                <div className={styles.detailsNameGrid}>
                  <Box className={styles.detailItem2}>
                    <Typography className={styles.detailLabel}>Date</Typography>
                    <Typography className={styles.detailValue}>
                      {request.fromDate ? formatDateToDashStyle(request.fromDate) : 'Not Available (N/A)'}
                    </Typography>
                  </Box>
                </div>

                {/* TIME */}
                <div className={styles.detailsNameGrid}>
                  <Box className={styles.detailItem2}>
                    <Typography className={styles.detailLabel}>
                      {request.requestType === 'Late Entry' ? 'Expected Arrival Time' : 'Expected Departure Time'}
                    </Typography>

                    <Typography className={styles.detailValue}>{safeToAmPm(request.toTime)}</Typography>
                  </Box>
                </div>
              </>
            )}

            {request.requestType === 'Leave' && (
              <>
                {(() => {
                  const safeParseDate = (dateStr) => {
                    if (!dateStr) return null;
                    const d = new Date(dateStr);
                    return isNaN(d.getTime()) ? null : d;
                  };

                  const fromDate = safeParseDate(request.fromDate);
                  const toDate = safeParseDate(request.toDate);

                  const numberOfDays =
                    fromDate && toDate ? Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1 : 'Not Available (N/A)';

                  const formatDisplayDate = (date) =>
                    date
                      ? date.toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })
                      : 'Not Available (N/A)';

                  return (
                    <>
                      <div className={styles.detailsNameGrid}>
                        <Box className={styles.detailItem2}>
                          <Typography className={styles.detailLabel}>From Date</Typography>
                          <Typography className={styles.detailValue}>{formatDisplayDate(fromDate)}</Typography>
                        </Box>
                      </div>

                      <div className={styles.detailsNameGrid}>
                        <Box className={styles.detailItem2}>
                          <Typography className={styles.detailLabel}>To Date</Typography>
                          <Typography className={styles.detailValue}>{formatDisplayDate(toDate)}</Typography>
                        </Box>
                      </div>

                      <div className={styles.detailsNameGrid}>
                        <Box className={styles.detailItem2}>
                          <Typography className={styles.detailLabel}>Number of Days</Typography>
                          <Typography className={styles.detailValue}>{numberOfDays}</Typography>
                        </Box>
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            <div className={styles.horizintalRule} />
            <Typography className={styles.sectionTitle}>Reason for Request</Typography>
            <span>{request.reason || 'Not Available (N/A)'}</span>
          </Box>
        </Box>

        {/* Department-wise Approval Details */}
        {/* <Box className={styles.detailsSection}>
          <Typography className={styles.sectionTitle}>Approval Details</Typography>
          <Box className={styles.approvalGrid}>
            <Box className={styles.approvalCard}>
              <Typography className={styles.approvalTitle}>Academic</Typography>
              <Box className={styles.approvalItem}>
                <Typography className={styles.detailLabel}>Status</Typography>
                {academicApproval?.status ? (
                  <Typography
                    className={styles.detailValue}
                    sx={{
                      color: getStatusColor(academicApproval.status).color,
                      fontWeight: 700
                    }}
                  >
                    {academicApproval.status}
                  </Typography>
                ) : (
                  <Typography className={styles.detailValue}>Not Available (N/A)</Typography>
                )}
              </Box>
              <Box className={styles.approvalItem}>
                <Typography className={styles.detailLabel}>Approved By</Typography>
                <Typography style={{ textTransform: 'capitalize' }} className={styles.detailValue}>
                  {getApproverName(academicApproval)}
                </Typography>
              </Box>
              <Box className={styles.approvalItem}>
                <Typography className={styles.detailLabel}>Role</Typography>
                <Typography className={styles.detailValue}>{getApproverRole(academicApproval)}</Typography>
              </Box>
            </Box>

            <Box className={styles.approvalCard}>
              <Typography className={styles.approvalTitle}>Hostel</Typography>
              <Box className={styles.approvalItem}>
                <Typography className={styles.detailLabel}>Status</Typography>
                {hostelApproval?.status ? (
                  <Typography
                    className={styles.detailValue}
                    sx={{
                      color: getStatusColor(hostelApproval.status).color,
                      fontWeight: 700
                    }}
                  >
                    {hostelApproval.status}
                  </Typography>
                ) : (
                  <Typography className={styles.detailValue}>Not Available (N/A)</Typography>
                )}
              </Box>
              <Box className={styles.approvalItem}>
                <Typography className={styles.detailLabel}>Approved By</Typography>
                <Typography style={{ textTransform: 'capitalize' }} className={styles.detailValue}>
                  {getApproverName(hostelApproval)}
                </Typography>
              </Box>
              <Box className={styles.approvalItem}>
                <Typography className={styles.detailLabel}>Role</Typography>
                <Typography className={styles.detailValue}>{getApproverRole(hostelApproval)}</Typography>
              </Box>
            </Box>
          </Box>
        </Box> */}

        {/* Approved or Rejected by */}
        {(String(request.status).toLowerCase() === 'approved' || String(request.status).toLowerCase() === 'rejected') && (
          <Box
            className={`${styles.detailsSection} ${
              String(request.status).toLowerCase() === 'rejected' ? styles.rejectedBy : styles.approvedBy
            }`}
          >
            <Typography className={styles.sectionTitle}>
              {String(request.status).toLowerCase() === 'approved' ? 'Approved By' : 'Rejected By'}
            </Typography>
            <Box className={styles.detailsGrid}>
              <div className={`${styles.detailsNameGrid} ${styles.detailsGrid2}`}>
                <Box className={styles.detailItem}>
                  <Typography className={styles.detailLabel}>Name</Typography>
                  <Typography style={{ textTransform: 'capitalize' }} className={styles.detailValue}>
                    {approver?.firstName ? `${approver.firstName} ${approver.lastName || ''}` : 'Not Available (N/A)'}
                  </Typography>
                </Box>
                <Box className={styles.detailItem}>
                  <Typography className={styles.detailLabel}>Login ID</Typography>
                  <Typography className={styles.detailValue}>{approver?.email || 'Not Available (N/A)'}</Typography>
                </Box>
                <Box className={styles.detailItem}>
                  <Typography className={styles.detailLabel}>Mobile number</Typography>
                  <Typography className={styles.detailValue}>{approver?.phoneNumber || 'Not Available (N/A)'}</Typography>
                </Box>
                <Box className={styles.detailItem}>
                  <Typography className={styles.detailLabel}>Remark</Typography>
                  <Typography className={styles.detailValue}>{request?.remark || 'Not Available (N/A)'}</Typography>
                </Box>
                <Box className={styles.detailItem}>
                  <Typography className={styles.detailLabel}>
                    {String(request.status).toLowerCase() === 'approved' ? 'Approved Time' : 'Rejected Time'}
                  </Typography>
                  <Typography className={styles.detailValue}>
                    {formatDateTime(
                      String(request.status).toLowerCase() === 'approved'
                        ? request?.approvedAt || request?.approvedTime || request?.updatedAt
                        : request?.rejectedAt || request?.rejectedTime || request?.updatedAt
                    )}
                  </Typography>
                </Box>
              </div>
            </Box>
          </Box>
        )}

         {/* Action Buttons for Pending Requests now it fixed and work perfectly */}
        {request.status === 'Pending' && (
          <Box className={styles.actionsContainer}>
            <Button variant="contained" color="error" className={styles.rejectBtn} onClick={onReject}>
              <X size={16} /> Reject Request
            </Button>
            <Button variant="contained" color="success" className={styles.resolveButton} onClick={onApprove}>
              <Check size={16} /> Approve Request
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ViewPassRequest;
